// pages/purchase-entry/BatchPreviewStep.tsx

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  AlertTriangle,
  AlertOctagon,
  AlertCircle,
  Loader2,
  Building2,
  FileText,
  Calendar,
  Eye,
  ExternalLink,
  Package,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Clock
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useInvoiceEntryStore,
  ToolCallParams,
  InvoiceData,
  InvoiceStatus
} from '@/stores/invoiceEntryStore';
import { useNavigationStore } from '@/stores/navigationStore';
import { getInvoiceLabels } from '@/utils/invoiceLabels';
import { LedgerCombobox } from './LedgerCombobox';
import { StockItemCombobox } from './StockItemCombobox';
import { GodownCombobox } from './GodownCombobox';
import { VoucherTypeCombobox } from './VoucherTypeCombobox';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || '';

type FilterType = 'all' | 'extracted' | 'posted' | 'failed';

export function BatchPreviewStep() {
  const {
    invoices,
    currentInvoiceIndex,
    company,
    batchId,
    totalCount,
    extractedCount,
    failedCount,
    duplicateCount,
    transactionType,
    setCurrentInvoiceIndex,
    updateInvoice,
    addInvoice,
    setExtractedCount,
    setFailedCount,
    setDuplicateCount,
    goToNextInvoice,
    goToPreviousInvoice,
    getCurrentInvoice,
    setCurrentStep,
    setError,
  } = useInvoiceEntryStore();

  // Force re-render trigger to ensure UI updates when invoices change
  const [, forceUpdate] = useState({});

  // Get dynamic labels based on transaction type
  const labels = getInvoiceLabels(transactionType);

  const { setSidebarCollapsed } = useNavigationStore();
  const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const pollingRefs = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  // Auto-collapse sidebar on mount
  useEffect(() => {
    const navigationStore = useNavigationStore.getState();
    if (!navigationStore.sidebarCollapsed) {
      navigationStore.setSidebarCollapsed(true);
    }

    return () => {
      // Restore sidebar on unmount
      navigationStore.setSidebarCollapsed(false);

      // Clear all polling intervals
      pollingRefs.current.forEach(interval => clearInterval(interval));
      pollingRefs.current.clear();
    };
  }, []);

  const currentInvoice = getCurrentInvoice();
  const toolCallParams = currentInvoice?.tool_call_params;

  // Filter invoices based on selected filter
  const filteredInvoices = invoices.filter(inv => {
    if (filter === 'all') return true;
    // "Extracted" means ready for review - extraction completed, not yet posted OR posted
    if (filter === 'extracted') return inv.extraction_status === 'completed';
    if (filter === 'posted') return inv.voucher_status === 'completed';
    if (filter === 'failed') return inv.extraction_status === 'failed' || inv.voucher_status === 'failed';
    return true;
  });

  // File type detection for current invoice
  const isImage = currentInvoice?.file_name?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/);
  const isPdf = currentInvoice?.file_name?.toLowerCase().endsWith('.pdf');

  // Get global dropdown data (shared by all invoices in batch)
  const { batchDropdownData } = useInvoiceEntryStore();
  const availableLedgers = batchDropdownData?.ledgers || [];
  const availableStockItems = batchDropdownData?.stockItems || [];
  const availableGodowns = batchDropdownData?.godowns || [];
  const availableGstLedgers = batchDropdownData?.gstLedgers || [];
  const availableExpenseLedgers = batchDropdownData?.expenseLedgers || [];
  const availableTransactionAccounts = batchDropdownData?.transactionAccounts || [];
  const availableVoucherTypes = batchDropdownData?.voucherTypes || [];

  // Calculate invoice status flags
  const isSuspense = toolCallParams?.party_name?.toLowerCase().includes('suspense');
  const isDuplicate = false; // Would need duplicate detection logic

  // Check if this invoice has stock items
  const hasStockItems = (toolCallParams?.stock_entries?.length || 0) > 0;

  // Calculate totals
  const stockTotal = toolCallParams?.stock_entries?.reduce((sum, e) => sum + e.amount, 0) || 0;
  const purchaseAccount = hasStockItems ? toolCallParams?.expense_ledgers?.[0] : null;
  const serviceExpenseLedgers = hasStockItems
    ? (toolCallParams?.expense_ledgers?.slice(1) || [])
    : (toolCallParams?.expense_ledgers || []);
  const serviceTotal = serviceExpenseLedgers.reduce((sum, e) => sum + (e.amount || 0), 0);
  const gstTotal = toolCallParams?.gst_ledgers?.reduce((sum, e) => sum + e.amount, 0) || 0;
  const grandTotal = stockTotal + serviceTotal + gstTotal;

  const handleViewDocument = () => {
    if (!currentInvoice) return;

    // Open PDF in new tab or show image in dialog
    if (isPdf && currentInvoice.file_url) {
      window.open(currentInvoice.file_url, '_blank');
    } else if (isImage) {
      setIsDocViewerOpen(true);
    }
  };

  // Update helpers - update current invoice's tool_call_params
  const updateToolCallParams = (updates: Partial<ToolCallParams>) => {
    if (!currentInvoice || !currentInvoice.tool_call_params) return;

    updateInvoice(currentInvoice.invoice_id, {
      tool_call_params: {
        ...currentInvoice.tool_call_params,
        ...updates
      }
    });
  };

  const updateParty = (value: string) => {
    updateToolCallParams({ party_name: value });
  };

  const updateNarration = (value: string) => {
    updateToolCallParams({ narration: value });
  };

  const updateInvoiceNumber = (value: string) => {
    updateToolCallParams({ invoice_number: value });
  };

  const updateInvoiceDate = (value: string) => {
    updateToolCallParams({ invoice_date: value });
  };

  const updateTransactionAccount = (value: string) => {
    if (!toolCallParams) return;
    const updated = [...toolCallParams.expense_ledgers];
    if (updated.length > 0) {
      updated[0] = { ...updated[0], ledger_name: value };
      updateToolCallParams({ expense_ledgers: updated });
    }
  };

  const updateStockEntry = (index: number, field: keyof NonNullable<ToolCallParams['stock_entries']>[0], value: string | number) => {
    if (!toolCallParams?.stock_entries) return;
    const updated = [...toolCallParams.stock_entries];
    if (field === 'quantity' || field === 'rate' || field === 'amount') {
      updated[index] = { ...updated[index], [field]: parseFloat(value as string) || 0 };
      // Recalculate amount if qty or rate changed
      if (field === 'quantity' || field === 'rate') {
        updated[index].amount = updated[index].quantity * updated[index].rate;
      }
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    updateToolCallParams({ stock_entries: updated });

    // Also update the expense ledger amount to match stock total
    const newStockTotal = updated.reduce((sum, e) => sum + e.amount, 0);
    const expenseUpdated = [...toolCallParams.expense_ledgers];
    if (expenseUpdated.length > 0) {
      expenseUpdated[0] = { ...expenseUpdated[0], amount: newStockTotal };
      updateToolCallParams({ expense_ledgers: expenseUpdated });
    }
  };

  const updateGstLedger = (index: number, field: keyof NonNullable<ToolCallParams['gst_ledgers']>[0], value: string | number) => {
    if (!toolCallParams?.gst_ledgers) return;
    const updated = [...toolCallParams.gst_ledgers];
    if (field === 'amount') {
      updated[index] = { ...updated[index], amount: parseFloat(value as string) || 0 };
    } else {
      updated[index] = { ...updated[index], [field]: value as string };
    }
    updateToolCallParams({ gst_ledgers: updated });
  };

  // Navigate to specific invoice
  const handleInvoiceClick = (index: number) => {
    setCurrentInvoiceIndex(index);
  };

  // Skip to next non-failed invoice
  const skipToNextValid = () => {
    let nextIndex = currentInvoiceIndex + 1;
    while (nextIndex < invoices.length) {
      if (invoices[nextIndex].extraction_status !== 'failed') {
        setCurrentInvoiceIndex(nextIndex);
        return;
      }
      nextIndex++;
    }
  };

  // Skip to previous non-failed invoice
  const skipToPreviousValid = () => {
    let prevIndex = currentInvoiceIndex - 1;
    while (prevIndex >= 0) {
      if (invoices[prevIndex].extraction_status !== 'failed') {
        setCurrentInvoiceIndex(prevIndex);
        return;
      }
      prevIndex--;
    }
  };

  // Poll voucher job status
  const pollVoucherJob = (invoiceId: string, jobId: string) => {
    // Clear existing polling for this invoice
    const existingInterval = pollingRefs.current.get(invoiceId);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    const pollStatus = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL}/api/jobs/${jobId}/status`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch job status');
        }

        const data = await response.json();

        if (data.status === 'completed') {
          // Update invoice with voucher number
          updateInvoice(invoiceId, {
            voucher_status: 'completed',
            voucher_number: data.output_data?.voucher_number || null,
          });

          // Stop polling
          const interval = pollingRefs.current.get(invoiceId);
          if (interval) {
            clearInterval(interval);
            pollingRefs.current.delete(invoiceId);
          }
        } else if (data.status === 'failed') {
          // Update invoice with error
          updateInvoice(invoiceId, {
            voucher_status: 'failed',
            error_message: data.error_message || 'Voucher creation failed',
          });

          // Stop polling
          const interval = pollingRefs.current.get(invoiceId);
          if (interval) {
            clearInterval(interval);
            pollingRefs.current.delete(invoiceId);
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    };

    // Initial poll
    pollStatus();

    // Poll every 3 seconds
    const interval = setInterval(pollStatus, 3000);
    pollingRefs.current.set(invoiceId, interval);
  };

  // Approve and post current invoice
  const handleApproveAndPost = async () => {
    if (!currentInvoice || !company || !toolCallParams) return;

    // Check for validation issues
    if (!toolCallParams.party_name || isDuplicate) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${API_URL}/api/invoice-entry/create-voucher`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            company,
            tool_call_params: toolCallParams,
            transaction_type: transactionType,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to approve invoice');
      }

      const data = await response.json();

      // Update invoice with job ID and status
      updateInvoice(currentInvoice.invoice_id, {
        voucher_job_id: data.job_id,
        voucher_status: 'processing',
      });

      // Start polling this voucher job
      pollVoucherJob(currentInvoice.invoice_id, data.job_id);

      // Auto-advance to next invoice
      skipToNextValid();
    } catch (err) {
      console.error('Approve error:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve invoice');
    }
  };

  // Get status badge for invoice
  const getStatusBadge = (invoice: InvoiceData) => {
    if (invoice.voucher_status === 'completed') {
      return (
        <Badge variant="default" className="bg-blue-500 text-white">
          Posted ✓ {invoice.voucher_number}
        </Badge>
      );
    }
    if (invoice.voucher_status === 'processing') {
      return (
        <Badge variant="secondary" className="text-yellow-600">
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          Posting...
        </Badge>
      );
    }
    if (invoice.voucher_status === 'failed') {
      return <Badge variant="destructive">Failed</Badge>;
    }
    if (invoice.extraction_status === 'completed') {
      return <Badge variant="outline" className="text-green-600 border-green-600">Ready for Review</Badge>;
    }
    if (invoice.extraction_status === 'failed') {
      return <Badge variant="destructive">Failed</Badge>;
    }
    return <Badge variant="secondary">Processing</Badge>;
  };

  const canApprove = currentInvoice &&
    currentInvoice.extraction_status === 'completed' &&
    !currentInvoice.voucher_status &&
    toolCallParams?.party_name &&
    !isDuplicate;

  const canNavigatePrev = currentInvoiceIndex > 0;
  const canNavigateNext = currentInvoiceIndex < invoices.length - 1;

  // Continue polling for remaining invoices in background
  useEffect(() => {
    if (!batchId) return;

    // Check if there are still pending invoices
    const hasPendingInvoices = extractedCount + failedCount + duplicateCount < totalCount;
    if (!hasPendingInvoices) return;

    const pollForMore = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL}/api/invoice-entry/batches/${batchId}/invoices`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const newInvoices: InvoiceStatus[] = await response.json();
          const currentInvoices = useInvoiceEntryStore.getState().invoices;

          newInvoices.forEach((inv) => {
            const exists = currentInvoices.find(existing => existing.invoice_id === inv.invoice_id);
            if (!exists) {
              addInvoice({
                invoice_id: inv.invoice_id,
                file_name: inv.file_name,
                file_url: inv.file_url,
                extraction_status: inv.extraction_status as any,
                extraction_job_id: inv.extraction_job_id,
                voucher_status: inv.voucher_status as any,
                voucher_job_id: inv.voucher_job_id,
                voucher_number: null,
                tool_call_params: inv.tool_call_params,
                error_message: inv.error_message,
                is_duplicate: inv.is_duplicate,
              });
            } else {
              updateInvoice(inv.invoice_id, {
                extraction_status: inv.extraction_status as any,
                voucher_status: inv.voucher_status as any,
                voucher_number: inv.voucher_number || null,
                voucher_job_id: inv.voucher_job_id || null,
                tool_call_params: inv.tool_call_params,
                error_message: inv.error_message,
                is_duplicate: inv.is_duplicate,
              });
            }
          });

          // Update batch counts
          const batchResponse = await fetch(`${API_URL}/api/invoice-entry/batches/${batchId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (batchResponse.ok) {
            const batchData = await batchResponse.json();
            setExtractedCount(batchData.extracted_count || 0);
            setFailedCount(batchData.failed_count || 0);
            setDuplicateCount(batchData.duplicate_count || 0);
          }
        }
      } catch (err) {
        console.error('Background polling error:', err);
      }
    };

    // Poll every 5 seconds for new completions
    const interval = setInterval(pollForMore, 5000);
    pollForMore(); // Initial poll

    return () => clearInterval(interval);
  }, [batchId, extractedCount, failedCount, duplicateCount, totalCount, updateInvoice, addInvoice, setExtractedCount, setFailedCount, setDuplicateCount]);

  return (
    <div className="flex h-[calc(100vh-200px)] gap-4">
      {/* Left Side: Invoice List (20%) */}
      <div className="w-1/5 border rounded-lg p-3 overflow-y-auto bg-muted/20">
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Invoices ({invoices.length})</h3>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as FilterType)}
              className="text-xs border rounded px-2 py-1"
            >
              <option value="all">All</option>
              <option value="extracted">Extracted</option>
              <option value="posted">Posted</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {filteredInvoices.map((invoice, idx) => {
            const originalIndex = invoices.findIndex(inv => inv.invoice_id === invoice.invoice_id);
            const isSelected = originalIndex === currentInvoiceIndex;
            const isFailed = invoice.extraction_status === 'failed';

            return (
              <div
                key={invoice.invoice_id}
                onClick={() => !isFailed && handleInvoiceClick(originalIndex)}
                className={cn(
                  'p-2 rounded border cursor-pointer transition-colors',
                  isSelected && 'border-primary bg-primary/10',
                  !isSelected && !isFailed && 'hover:bg-muted/50',
                  isFailed && 'opacity-50 cursor-not-allowed'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate" title={invoice.file_name}>
                      {invoice.file_name}
                    </p>
                    <div className="mt-1">
                      {getStatusBadge(invoice)}
                    </div>
                  </div>
                  {isSelected && (
                    <div className="flex-shrink-0">
                      <ChevronRight className="w-4 h-4 text-primary" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Side: Preview (80%) */}
      <div className="flex-1 border rounded-lg p-4 overflow-y-auto">
        {!currentInvoice ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <p>No invoice selected</p>
          </div>
        ) : currentInvoice.extraction_status === 'failed' ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <XCircle className="w-16 h-16 text-red-500" />
            <div className="text-center">
              <h3 className="text-lg font-semibold text-red-800">Extraction Failed</h3>
              <p className="text-sm text-red-600 mt-2">{currentInvoice.error_message}</p>
            </div>
          </div>
        ) : currentInvoice.is_duplicate ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <AlertCircle className="w-16 h-16 text-blue-500" />
            <div className="text-center max-w-md">
              <h3 className="text-lg font-semibold text-blue-800">Invoice Already Exists</h3>
              <p className="text-sm text-blue-700 mt-2">
                {currentInvoice.error_message || 'This invoice already exists in Tally.'}
              </p>
            </div>
          </div>
        ) : !toolCallParams ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <p>No invoice data available</p>
          </div>
        ) : (
          <div key={currentInvoice.invoice_id} className="space-y-4">
            {/* Header - Invoice#, Date, Supplier, View Source */}
            <div className="flex flex-wrap items-end gap-4 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">{labels.invoiceNumberLabel}</p>
                  <Input
                    value={toolCallParams.invoice_number || ''}
                    onChange={(e) => updateInvoiceNumber(e.target.value)}
                    className="h-8 w-32"
                    placeholder={labels.invoiceNumberLabel}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">{labels.invoiceDateLabel}</p>
                  <Input
                    type="date"
                    value={toolCallParams.invoice_date || toolCallParams.date}
                    onChange={(e) => updateInvoiceDate(e.target.value)}
                    className="h-8 w-36"
                  />
                </div>
              </div>
              <div className="flex-1 min-w-[200px] max-w-[300px]">
                <div className="flex items-center gap-1 mb-1">
                  <Building2 className="h-3 w-3 text-muted-foreground" />
                  <label className="text-xs text-muted-foreground">{labels.partyLabel}</label>
                  {isSuspense && <Badge variant="destructive" className="text-xs ml-2">Suspense</Badge>}
                </div>
                <LedgerCombobox
                  value={toolCallParams.party_name}
                  onChange={updateParty}
                  ledgers={availableLedgers}
                  placeholder={labels.partyPlaceholder}
                />
              </div>
              {/* View Source Document Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleViewDocument}
                className="gap-1 h-8"
              >
                {isPdf ? (
                  <>
                    <ExternalLink className="h-3 w-3" />
                    View PDF
                  </>
                ) : (
                  <>
                    <Eye className="h-3 w-3" />
                    View Image
                  </>
                )}
              </Button>
            </div>

            {/* Image Viewer Dialog */}
            <Dialog open={isDocViewerOpen} onOpenChange={setIsDocViewerOpen}>
              <DialogContent className="max-w-4xl max-h-[90vh]">
                <DialogHeader>
                  <DialogTitle>Source Document: {currentInvoice?.file_name}</DialogTitle>
                </DialogHeader>
                <div className="overflow-auto max-h-[70vh] flex items-center justify-center">
                  {currentInvoice?.file_url && isImage ? (
                    <img
                      src={currentInvoice.file_url}
                      alt={currentInvoice.file_name}
                      className="max-w-full h-auto"
                    />
                  ) : (
                    <p className="text-muted-foreground">Image preview not available</p>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Transaction Account (Debit/Credit Ledger) - shown above stock items */}
            {purchaseAccount && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  {labels.transactionAccountLabel}
                </h3>
                <div className="p-3 border rounded-lg bg-muted/30">
                  <LedgerCombobox
                    value={purchaseAccount.ledger_name}
                    onChange={updateTransactionAccount}
                    ledgers={availableTransactionAccounts}
                    placeholder={labels.transactionAccountPlaceholder}
                  />
                </div>
              </div>
            )}

            {/* Stock Entries Table */}
            {toolCallParams.stock_entries && toolCallParams.stock_entries.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Stock Items ({toolCallParams.stock_entries.length})
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[30%]">Stock Item</TableHead>
                        <TableHead className="w-[15%]">Godown</TableHead>
                        <TableHead className="w-[12%] text-right">Qty</TableHead>
                        <TableHead className="w-[8%]">Unit</TableHead>
                        <TableHead className="w-[15%] text-right">Rate</TableHead>
                        <TableHead className="w-[15%] text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {toolCallParams.stock_entries.map((entry, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <StockItemCombobox
                              value={entry.stock_item}
                              onChange={(val) => updateStockEntry(idx, 'stock_item', val || '')}
                              stockItems={availableStockItems}
                              placeholder="Select item..."
                            />
                          </TableCell>
                          <TableCell>
                            <GodownCombobox
                              value={entry.godown || ''}
                              onChange={(val) => updateStockEntry(idx, 'godown', val)}
                              godowns={availableGodowns}
                              placeholder="Select godown..."
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              value={entry.quantity}
                              onChange={(e) => updateStockEntry(idx, 'quantity', e.target.value)}
                              className="h-8 w-20 text-right"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={entry.unit || ''}
                              onChange={(e) => updateStockEntry(idx, 'unit', e.target.value)}
                              className="h-8 w-16"
                              placeholder="Unit"
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              value={entry.rate}
                              onChange={(e) => updateStockEntry(idx, 'rate', e.target.value)}
                              className="h-8 w-24 text-right"
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {entry.amount.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Service Expense Ledgers Table */}
            {serviceExpenseLedgers.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Service Expenses ({serviceExpenseLedgers.length})
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[70%]">Expense Ledger</TableHead>
                        <TableHead className="w-[30%] text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {serviceExpenseLedgers.map((entry, idx) => (
                        <TableRow key={idx} className="bg-muted/30">
                          <TableCell>
                            <LedgerCombobox
                              value={entry.ledger_name}
                              onChange={(val) => {
                                const updated = [...toolCallParams.expense_ledgers];
                                updated[idx + 1] = { ...updated[idx + 1], ledger_name: val };
                                updateToolCallParams({ expense_ledgers: updated });
                              }}
                              ledgers={availableExpenseLedgers}
                              placeholder="Select expense ledger..."
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              value={entry.amount || 0}
                              onChange={(e) => {
                                const updated = [...toolCallParams.expense_ledgers];
                                updated[idx + 1] = { ...updated[idx + 1], amount: parseFloat(e.target.value) || 0 };
                                updateToolCallParams({ expense_ledgers: updated });
                              }}
                              className="h-8 w-28 text-right"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* GST Ledgers Table */}
            {toolCallParams.gst_ledgers && toolCallParams.gst_ledgers.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium">GST Ledgers ({toolCallParams.gst_ledgers.length})</h3>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[70%]">GST Ledger</TableHead>
                        <TableHead className="w-[15%]">Type</TableHead>
                        <TableHead className="w-[15%] text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {toolCallParams.gst_ledgers.map((entry, idx) => (
                        <TableRow key={idx} className="bg-muted/30">
                          <TableCell>
                            <LedgerCombobox
                              value={entry.ledger_name}
                              onChange={(val) => updateGstLedger(idx, 'ledger_name', val)}
                              ledgers={availableGstLedgers}
                              placeholder="Select GST ledger..."
                            />
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">GST</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              value={entry.amount}
                              onChange={(e) => updateGstLedger(idx, 'amount', e.target.value)}
                              className="h-8 w-28 text-right"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Narration */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Narration</label>
              <Textarea
                value={toolCallParams.narration || ''}
                onChange={(e) => updateNarration(e.target.value)}
                placeholder="Enter voucher narration..."
                className="min-h-[80px]"
              />
            </div>

            {/* Totals - Stock Items + Services + GST */}
            <div className="flex justify-end">
              <div className="w-64 space-y-1 text-sm">
                {stockTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Stock Items</span>
                    <span>{stockTotal.toFixed(2)}</span>
                  </div>
                )}
                {serviceTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Services</span>
                    <span>{serviceTotal.toFixed(2)}</span>
                  </div>
                )}
                {gstTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">GST</span>
                    <span>{gstTotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium text-base pt-2 border-t">
                  <span>Grand Total</span>
                  <span>{grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Duplicate Invoice Warning */}
            {isDuplicate && (
              <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertOctagon className="h-5 w-5 text-red-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-red-800">Duplicate Invoice Detected</p>
                    <p className="mt-1 text-red-700">
                      This invoice may already exist in Tally. Creating this voucher could result in a duplicate entry.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Suspense Warning */}
            {isSuspense && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Suspense Account</p>
                    <p className="mt-1 text-amber-700">
                      The {labels.partyLabel.toLowerCase()} could not be matched to an existing ledger. Please select the correct {labels.partyLabel.toLowerCase()}.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Voucher Type Selector */}
            {toolCallParams.voucher_type !== undefined && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Voucher Type:</span>
                <div className="w-64">
                  <VoucherTypeCombobox
                    value={toolCallParams.voucher_type || ''}
                    onChange={(val) => updateToolCallParams({ voucher_type: val })}
                    voucherTypes={availableVoucherTypes}
                    placeholder="Select voucher type..."
                  />
                </div>
              </div>
            )}

            {/* Navigation and Actions */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={skipToPreviousValid}
                  disabled={!canNavigatePrev}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Invoice {currentInvoiceIndex + 1} of {invoices.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={skipToNextValid}
                  disabled={!canNavigateNext}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>

              <Button
                onClick={handleApproveAndPost}
                disabled={!canApprove}
                className="gap-2"
              >
                {currentInvoice.voucher_status === 'processing' ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {labels.voucherCreatingLabel}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    {labels.voucherButtonLabel}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
